// Republic Atlas - Office Hooks

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { officeService, type OfficeFilters } from '@/services';

// Cache times
const OFFICE_STALE_TIME = 24 * 60 * 60 * 1000; // 24 hours
const OFFICE_CACHE_TIME = 48 * 60 * 60 * 1000; // 48 hours

/**
 * Hook to fetch all offices with filters
 */
export function useOffices(filters?: OfficeFilters) {
  return useQuery({
    queryKey: ['offices', filters],
    queryFn: () => officeService.getOffices(filters),
    staleTime: OFFICE_STALE_TIME,
    gcTime: OFFICE_CACHE_TIME,
  });
}

/**
 * Hook to fetch a single office by ID
 */
export function useOffice(id: string | undefined) {
  return useQuery({
    queryKey: ['office', id],
    queryFn: () => officeService.getOffice(id!),
    enabled: !!id,
    staleTime: OFFICE_STALE_TIME,
    gcTime: OFFICE_CACHE_TIME,
  });
}

/**
 * Hook to fetch offices by jurisdiction
 */
export function useOfficesByJurisdiction(jurisdictionId: string | undefined) {
  return useQuery({
    queryKey: ['offices', 'jurisdiction', jurisdictionId],
    queryFn: () => officeService.getOfficesByJurisdiction(jurisdictionId!),
    enabled: !!jurisdictionId,
    staleTime: OFFICE_STALE_TIME,
    gcTime: OFFICE_CACHE_TIME,
  });
}

/**
 * Hook to search offices
 */
export function useOfficeSearch(query: string) {
  return useQuery({
    queryKey: ['offices', 'search', query],
    queryFn: () => officeService.searchOffices(query),
    enabled: query.length >= 2,
    staleTime: 5 * 60 * 1000, // 5 minutes for search results
  });
}

/**
 * Hook to prefetch office data
 */
export function usePrefetchOffice() {
  const queryClient = useQueryClient();
  
  return (id: string) => {
    queryClient.prefetchQuery({
      queryKey: ['office', id],
      queryFn: () => officeService.getOffice(id),
      staleTime: OFFICE_STALE_TIME,
    });
  };
}

/**
 * Hook to invalidate office cache
 */
export function useInvalidateOffices() {
  const queryClient = useQueryClient();
  
  return () => {
    queryClient.invalidateQueries({ queryKey: ['offices'] });
  };
}

